import { Inbox01 } from '@untitledui/icons';

import { PageLayout } from '@/lib/base/page-layout';

import type { SeatUpgradeCardProps } from './SeatUpgradeCard';
import { SeatUpgradeCard } from './SeatUpgradeCard';

const PAGE_TITLE: Record<SeatUpgradeCardProps['feature'], string> = {
  ai_actions: 'AI Actions',
  chat: 'Chat',
  crm_export: 'Export',
};

/**
 * Three ghost rows standing in for the list the viewer would see with a full
 * seat. Pure decoration — no data, no interaction — faded out behind the card
 * so the page still reads as "the AI Actions page" rather than an empty void.
 */
function GhostRows() {
  return (
    <div
      aria-hidden='true'
      className='pointer-events-none absolute inset-x-0 top-0 flex flex-col gap-3 px-4 pt-2 [mask-image:linear-gradient(to_bottom,rgba(0,0,0,0.55),transparent_85%)]'
    >
      {[0, 1, 2].map(row => (
        <div
          key={row}
          className='flex items-center gap-4 rounded-xl border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] px-4 py-3.5'
        >
          <span className='size-8 shrink-0 rounded-full bg-[var(--color-bg-tertiary)]' />
          <span className='flex flex-1 flex-col gap-2'>
            <span className='h-2.5 w-2/5 rounded bg-[var(--color-bg-tertiary)]' />
            <span className='h-2 w-3/5 rounded bg-[var(--color-bg-secondary)]' />
          </span>
          <span className='h-7 w-20 shrink-0 rounded-md bg-[var(--color-bg-tertiary)]' />
        </div>
      ))}
    </div>
  );
}

/**
 * Full-page gate shown to viewer-seat users on routes that require a full seat
 * (e.g. the AI Actions route). Keeps the destination's own page chrome — the
 * same title row the real list renders — so the viewer knows where they are,
 * and centers the upgrade card over a faded stand-in for the list itself.
 */
export function ViewerUpgradeScreen({
  feature,
}: Pick<SeatUpgradeCardProps, 'feature'>) {
  return (
    <PageLayout
      title={PAGE_TITLE[feature]}
      icon={<Inbox01 className='size-5 text-fg-quaternary' />}
    >
      <div className='relative flex flex-1 flex-col items-center px-4 pb-8 pt-16 sm:pt-24'>
        <GhostRows />
        <div className='relative w-full max-w-md'>
          <SeatUpgradeCard feature={feature} variant='full' />
        </div>
      </div>
    </PageLayout>
  );
}
